/**
 * RisksSection — деньги под риском, крупнейшая кампания первой.
 *
 * Величины приходят суточными: кампания попала в список потому, что в
 * последний день периода в ней сработали критичные сенсоры. На экран они
 * выходят умноженными на `RISK_DAYS` — это проекция, а не факт, и
 * подпись под суммой прямо это говорит.
 *
 * Простое объяснение открыто: оно для директора, ради него этаж и
 * существует. Комментарий специалисту и разбор по сенсорам — под
 * раскрытием: в отчёте, который читают раз в месяц, они нужны не всем и
 * не сразу.
 *
 * @format
 */

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {RISK_DAYS} from '../content';
import {useReportFormat} from '../format';
import {MEASURE, TABULAR} from '../helpers';
import type {RiskRestRow, RiskRow} from '../types';
import {Disclosure} from './Disclosure';
import {SpendBlock, SpendRowAmount, SpendRowMeta, SpendRowName} from './SpendBlock';
import {SplitCard, SplitPanel} from './SplitCard';

/** Неразрывный пробел: escape, а не живой символ — его стирает eslint --fix. */
const NBSP = '\u00A0';

/**
 * Строки, которые секция реально отрисует.
 *
 * Строка без расхода пропускается: сумма — причина, по которой кампания
 * вообще попала в список. `ExecutiveReport.tsx` считает `hasRisks` этой же
 * функцией: иначе этаж мог бы решить рисоваться по голому `risks.length`,
 * а секция ниже — не нарисовать ни одной строки, и читатель получил бы
 * сумму, а под ней пустую карточку.
 */
export function visibleRiskRows(rows: RiskRow[]): RiskRow[] {
  return rows.filter(row => row.spend !== null && row.spend !== undefined);
}

export interface RisksSectionProps {
  /** Суточный расход кампаний под риском. */
  risk: number;
  /** Суточный расход всех кампаний — знаменатель доли. */
  dailySpend: number;
  /** Доля в процентах; приходит готовой. */
  pct?: number | null;
  rows: RiskRow[];
  rest?: RiskRestRow[] | null;
  /** Объяснение нулевого риска: приходит вместо строк, а не вместе с ними. */
  note?: string | null;
  /** Главная тревога сенсоров, не выраженная в деньгах. */
  sensorWarning?: string | null;
}

const Simple = styled(Box)({
  ...text('body.l'),
  color: surfaceVar('onSurface'),
  maxWidth: MEASURE,
});

const Professional = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceLight'),
  maxWidth: MEASURE,
});

/** Заголовок списка сенсоров внутри комментария: он там не единственный блок. */
const SensorsCaption = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceMuted'),
});

const SensorLine = styled(Row)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceLight'),
});

const SensorCount = styled(Box)({
  ...TABULAR,
  color: surfaceVar('onSurfaceMuted'),
});

const Note = styled(Box)({
  ...text('body.l'),
  color: surfaceVar('onSurface'),
  maxWidth: MEASURE,
});

const Warning = styled(Box)(({theme}) => ({
  ...text('body.m'),
  color: theme.palette.alert.main,
  maxWidth: MEASURE,
}));

function RiskLine({row}: {row: RiskRow}) {
  const fmt = useReportFormat();
  const sensors = row.sensors ?? [];

  return (
    <SplitPanel padding='l'>
      {/* `nowrap` (по умолчанию) — не `wrap`: соседний `Stack` по контракту
          занимает 100% ширины контейнера (Stack.tsx:58), и с `wrap` браузер
          переносил бы его на новую строку вместо того, чтобы сжать до
          остатка ширины после колонки суммы. `reflowAt='md'` сворачивает
          ряд в столбец на узком экране — там многоабзацному содержимому
          нужна полная ширина, а не сжатая полоса рядом с суммой. */}
      <Row gap='m' alignY='start' reflowAt='md'>
        <SpendRowAmount>{fmt.money((row.spend ?? 0) * RISK_DAYS)}</SpendRowAmount>
        <Stack gap='s'>
          <Stack gap='xxs'>
            <SpendRowName>{row.name || row.campaignId}</SpendRowName>
            {/* «критичных сенсоров» — последние два слова фразы длиннее двух
                слов (typography.md §1); число и «срабатываний» разделяет
                обычный пробел — перенос между ними не портит чтение. */}
            <SpendRowMeta>{`${row.criticalFound} срабатываний критичных${NBSP}сенсоров`}</SpendRowMeta>
          </Stack>

          {row.simple && <Simple>{row.simple}</Simple>}

          {/* Одно раскрытие на всю экспертную часть.
              Два подряд заставляли открывать дважды, чтобы собрать одну
              мысль: комментарий объясняет, что происходит, а список
              сенсоров — на чём это увидено. Порознь ни то, ни другое не
              полно. */}
          {(row.professional || sensors.length > 0) && (
            <Disclosure label='Профессиональный комментарий' labelOpen='Свернуть'>
              <Stack gap='m'>
                {row.professional && <Professional>{row.professional}</Professional>}
                {sensors.length > 0 && (
                  <Stack gap='xxs'>
                    <SensorsCaption>Сработавшие сенсоры</SensorsCaption>
                    {sensors.map(sensor => (
                      <SensorLine key={sensor.key} gap='s' justify='between'>
                        <Box>{sensor.name}</Box>
                        <SensorCount>{sensor.found}</SensorCount>
                      </SensorLine>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Disclosure>
          )}
        </Stack>
      </Row>
    </SplitPanel>
  );
}

export function RisksSection({
  risk,
  dailySpend,
  pct,
  rows,
  rest,
  note,
  sensorWarning,
}: RisksSectionProps) {
  const visible = visibleRiskRows(rows);
  // Тот же фильтр — до подсчёта, а не после: иначе сумма и счётчик хвоста
  // считались бы по разным наборам (сумма пропускала бы `null` через
  // `?? 0`, а счёт брал бы длину списка целиком).
  const restRows = (rest ?? []).filter(row => row.spend !== null && row.spend !== undefined);
  const restSum = restRows.reduce((sum, row) => sum + (row.spend ?? 0), 0);

  if (visible.length === 0 && note) {
    return (
      <Stack gap='m'>
        <Note>{note}</Note>
        {sensorWarning && <Warning>{sensorWarning}</Warning>}
      </Stack>
    );
  }

  return (
    <Stack gap='m'>
      <SpendBlock
        amount={risk * RISK_DAYS}
        of={dailySpend * RISK_DAYS}
        share={pct}
        // «за» (предлог) липнет к «30» (typography.md §2); «30 дней» —
        // последние два слова фразы длиннее двух слов (§1) — NBSP и там.
        caption={`При текущих настройках, за${NBSP}${RISK_DAYS}${NBSP}дней`}
        rest={
          restRows.length > 0
            ? {
                sum: restSum * RISK_DAYS,
                count: restRows.length,
                // Суммы кампаний приводим к тому же сроку, что и итог
                // хвоста: иначе строки таблицы не сложатся в его число.
                rows: restRows.map(row => ({
                  key: row.campaignId,
                  name: row.name || row.campaignId,
                  spend: (row.spend ?? 0) * RISK_DAYS,
                })),
              }
            : null
        }
      >
        <SplitCard direction='column' reflowAt='never'>
          {visible.map(row => (
            <RiskLine key={row.campaignId} row={row} />
          ))}
        </SplitCard>
      </SpendBlock>

      {sensorWarning && <Warning>{sensorWarning}</Warning>}
    </Stack>
  );
}
